// @ts-strict-ignore
import { cloneDeep } from 'lodash-es';
import { SecondaryProductionAmount } from '../../model/calculation-result';
import { EvaluatedFactor } from '../../model/factor';
import { IResource, ResourceType } from '../../model/resource';
import { getValueFromFactor, instantiateFactor } from '../../utils/factor-util';
import { KeyMap } from '../key-map';
import { Variable } from '../variable/variable';

export const DISTRICT_HEAT = 'district_heat';
export const ELECTRICITY = 'electricity';
export const SUPPLY_RESOURCE_GROUPS = [DISTRICT_HEAT, ELECTRICITY];

export interface ProducedByData {
  amount: number;
  origin: string;
  sourceCccId: string;
  emissionFactor: number;
  consideredPrimaryProduction: boolean;
  primaryResource?: string;
}

export class Resource {
  private _amount: number;
  private _conversions: EvaluatedFactor[];
  private _combinedEmissionFactor: number;
  private _defaultEmissionFactorBau: number | undefined;
  private _demand: number;
  private _emissionFactorBau: number;
  private _hasFixedEmissionFactor: boolean = false;
  private _isExclusivelyPrimaryProduced: boolean;
  private _name: string;
  private _nativeUnit: string;
  private _overridingEmissionFactor: Variable | undefined = undefined;
  private _producedBy: ProducedByData[];
  private _type: ResourceType;
  private _unit: string;

  get amount(): number {
    return this._amount;
  }

  get combinedEmissionFactor(): number {
    return this._combinedEmissionFactor;
  }

  get demand(): number {
    return this._demand;
  }

  get emissionFactor(): number {
    return this._overridingEmissionFactor ? this._overridingEmissionFactor.value : this._combinedEmissionFactor; // Only applicable on ResourceType.GridSuppliedEnergy
  }

  get emissionFactorBau(): number {
    return this._overridingEmissionFactor ? this._overridingEmissionFactor.referenceValue : this._emissionFactorBau;
  }

  get isExclusivelyPrimaryProduced(): boolean {
    return this._isExclusivelyPrimaryProduced;
  }

  get nativeUnit(): string {
    return this._nativeUnit;
  }

  get producedBy(): ProducedByData[] | undefined {
    return this._producedBy.length > 0 ? cloneDeep(this._producedBy) : undefined;
  }

  get type(): ResourceType {
    return this._type;
  }

  get unit(): string {
    return this._unit;
  }

  constructor(
    value: IResource,
    defaultEmissionFactorBau?: number,
    hasFixedEmissionFactor?: boolean,
    overridingEmissionFactor?: Variable
  ) {
    this._name = value.resource;
    this._nativeUnit = value.standardUnitOfMeasure;
    this._overridingEmissionFactor = overridingEmissionFactor;
    this._unit = value.standardUnitOfMeasure;
    this._type = <ResourceType>value.type;
    this._amount = 0;
    this._defaultEmissionFactorBau = defaultEmissionFactorBau;
    this._resetEmissionFactors();
    this._hasFixedEmissionFactor = !!hasFixedEmissionFactor;
    if (value.conversions) {
      this._conversions = value.conversions.map((c) => instantiateFactor(c));
    } else {
      this._conversions = [];
    }
    this._producedBy = [];
  }

  alterAmount(diff: number) {
    this._amount += diff;
  }

  getNativeConsumption(kwhConsumption: number, variables: KeyMap<Variable>): number {
    if (this._unit === 'kwh' && this._nativeUnit === 'kwh') {
      return kwhConsumption;
    }
    const nativeUnitConversionFactor = this._conversions.find((c) => c.unitOfMeasure === 'kwh');
    const nativeUnitConversionValue = getValueFromFactor(nativeUnitConversionFactor, variables);
    return kwhConsumption / nativeUnitConversionValue;
  }

  getSecondaryProductionData(): SecondaryProductionAmount[] {
    const secondaryProducedBy = this._producedBy.filter((v) => !v.consideredPrimaryProduction);
    const resources = new Map<string, number>();
    secondaryProducedBy.forEach((p) => {
      if (!resources.has(p.primaryResource)) {
        resources.set(p.primaryResource, 0);
      }
      resources.set(p.primaryResource, resources.get(p.primaryResource) + p.amount);
    });
    const secondaryProductionData = [];
    resources.forEach((amount, primaryResource) => secondaryProductionData.push({ primaryResource, amount }));
    return secondaryProductionData;
  }

  produceResource(
    t: number,
    change: number,
    originResource: string,
    sourceCccId: string,
    emissionFactorFromOrigin: number,
    primaryResourceProduced?: string,
    consideredPrimaryProduction: boolean = false
  ): void {
    if (change) {
      this._amount += change;
      const proportion = change / this._amount;
      const proportionateChange = isFinite(proportion) ? proportion : 0;
      let primaryResource: string;
      if (!consideredPrimaryProduction) {
        this._isExclusivelyPrimaryProduced = false;
        primaryResource = primaryResourceProduced;
      }
      this._producedBy.push({
        amount: change,
        origin: originResource,
        sourceCccId,
        emissionFactor: emissionFactorFromOrigin,
        primaryResource,
        consideredPrimaryProduction,
      });
      if (!this._hasFixedEmissionFactor) {
        this._combinedEmissionFactor =
          this._combinedEmissionFactor * (1 - proportionateChange) + proportionateChange * emissionFactorFromOrigin;
        if (t === 0) {
          this._emissionFactorBau = this._combinedEmissionFactor;
        }
      }
    }
  }

  registerDemand(resourceDemand: number) {
    if (isNaN(resourceDemand)) {
      resourceDemand = 0;
    }
    this._demand += resourceDemand;
  }

  reset(): void {
    this._amount = 0;
    if (!this._hasFixedEmissionFactor) {
      this._resetEmissionFactors();
    }
    this._isExclusivelyPrimaryProduced = true;
    this._demand = 0;
    this._producedBy = [];
  }

  private _resetEmissionFactors() {
    this._combinedEmissionFactor = this._defaultEmissionFactorBau || 0;
    this._emissionFactorBau = this._defaultEmissionFactorBau || 0;
  }
}
